import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";
import { GRID_ACTION_TYPES, GridActionType } from "../../../../assets/types";
import {
    ClaimHelper,
    isGridActionType,
} from "../../../../assets/utils/helpers";
import { Constants } from "../../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../../assets/utils/lib/error";
import { MessageSeverity } from "../../../../assets/utils/lib/messageSeverity";
import { Dialog, LoadingIndicator } from "../../../../assets/vfx";
import { useSnackbar } from "../../../../assets/vfx/snackbar/vfxSnackbarContext";
import { useActionFromQuery } from "../../../../hooks/useActionFromQuery";
import { OrganizationDto } from "../../../../services/dto/communityResource/organizationDto";
import { OrganizationForm } from "./organizationForm";
import { ResourceOrganizationPage } from "./organizationPage";
import { ResourceOrganizationService } from "./organizationService";
import { ResourceOrganizationView } from "./organizationView";
import { ViewProps } from "./props";

export const OrganizationContainer = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const { communityId: routeCommunityId } = useParams<{ communityId?: string; }>();
    const claimCommunityId = !routeCommunityId ? ClaimHelper.getCommunityId() : undefined;

    const [data, setData] = React.useState<OrganizationDto>();
    const [drawerType, setDrawerType] = React.useState<GridActionType>(GRID_ACTION_TYPES.HIDDEN);
    const [communityId, setCommunityId] = React.useState<string | undefined>(routeCommunityId ?? claimCommunityId);
    const [showDialog, setShowDialog] = React.useState<boolean>(false);

    const resourceOrganizationQuery = ResourceOrganizationService.getResourceOrganizationQuery(data?.id ?? Constants.emptyGuid);
    const lookupQuery = ResourceOrganizationService.getLookups(communityId);

    const { mutateAsync: saveOrganization } = ResourceOrganizationService.save();
    const { mutate: deActivateOrganization } = ResourceOrganizationService.deActivateOrganization();
    const { mutate: deleteMutate } = ResourceOrganizationService.deleteOrganization();

    const isEditMode = !!(data?.id && data.id !== Constants.emptyGuid);
    const closeDrawer = (): void => {
        setDrawerType(GRID_ACTION_TYPES.HIDDEN);
        clearSearchAction();
    }

    const effectRan = React.useRef(false)

    React.useEffect(() => {
        if (!effectRan.current) {
            if (communityId && communityId !== Constants.emptyGuid) {
                lookupQuery.refetch();
            }

            effectRan.current = true;
        }
    }, [communityId]);

    const refreshGrid = (): void => {
        isEditMode && resourceOrganizationQuery.refetch();
        queryClient.invalidateQueries({
            queryKey: ["resourceOrganizationGridQuery"],
        });
        closeDrawer();
    };

    const handleApiResponse = (response: any, successMessage: string): void => {
        const errorMsg = apiErrorMessage(response.errorMessages);
        if (errorMsg) {
            showSnackbar(errorMsg, MessageSeverity.Error);
        } else {
            showSnackbar(successMessage, MessageSeverity.Success);
            refreshGrid();
        }
    };

    const handleSubmit = async (values: OrganizationDto) => {
        values.id = isEditMode ? data!.id : Constants.emptyGuid;
        const message = isEditMode ? "Organization updated" : "Organization saved";
        return await saveOrganization(values, {
            onSuccess: (response) => handleApiResponse(response, message),
        });
    };

    const handleDelete = (): void => {
        deleteMutate(data?.id ?? Constants.emptyGuid, {
            onSuccess: (response) =>
                handleApiResponse(response, "Organization deleted"),
        });
    };

    const handleCustomEvent = (actionType: string, dataId: string): void => {
        if (isGridActionType(actionType)) {
            setDrawerType(actionType);
            setData({ id: dataId } as OrganizationDto);
            setSearchAction(actionType, dataId);
        }
        if (actionType == "delete") {
            setData(({ id: dataId } as OrganizationDto));
            setShowDialog(true);
        }
    };

    const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

    const handleEdit = (): void => {
        setData(resourceOrganizationQuery.data);
        setDrawerType(GRID_ACTION_TYPES.EDIT);
        setSearchAction(GRID_ACTION_TYPES.EDIT, resourceOrganizationQuery.data?.id);
    };

    const handleDeactivate = (): void => {
        const message: string = "Organization deactivated";

        deActivateOrganization(data?.id ?? Constants.emptyGuid, {
            onSuccess: (response) => handleApiResponse(response, message),
        });
    };

    const renderDrawerContent = (): React.JSX.Element | null => {
        switch (drawerType) {
            case GRID_ACTION_TYPES.NEW:
                return (
                    <OrganizationForm
                        show={true}
                        onHide={closeDrawer}
                        onSave={handleSubmit}
                        lookups={lookupQuery?.data}
                        communityId={communityId}
                        disableCommunity={!!routeCommunityId || !!claimCommunityId}
                    />
                );

            case GRID_ACTION_TYPES.EDIT:
                return (
                    <OrganizationForm
                        data={resourceOrganizationQuery.data}
                        loading={resourceOrganizationQuery.isLoading}
                        show={!!resourceOrganizationQuery.data}
                        onHide={closeDrawer}
                        onSave={handleSubmit}
                        lookups={lookupQuery?.data}
                        communityId={communityId}
                        disableCommunity={!!routeCommunityId || !!claimCommunityId}
                    />
                );

            case GRID_ACTION_TYPES.VIEW:
            case GRID_ACTION_TYPES.DOUBLE_CLICK:
                return (
                    <ResourceOrganizationView
                        data={resourceOrganizationQuery?.data}
                        show={!!resourceOrganizationQuery?.data}
                        onHide={closeDrawer}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        lookups={lookupQuery?.data}
                        onDeactivate={handleDeactivate}
                    />
                );

            default:
                return null;
        }
    };

    if (lookupQuery?.isLoading) {
        return <LoadingIndicator isFullPage />;
    }

    return (
        <>
            <ResourceOrganizationPage
                communityId={routeCommunityId ?? claimCommunityId}
                lookups={lookupQuery?.data}
                hideTitle={!!routeCommunityId}
                setCommunity={setCommunityId}
                onCustomEvent={handleCustomEvent}
            />
            {renderDrawerContent()}
            {
                <Dialog
                    contentText={"Are you sure you want to delete?"}
                    contentTitle={"Delete"}
                    onClose={() => setShowDialog(false)}
                    onConfirm={() => {
                        setShowDialog(false);
                        handleDelete();
                    }}
                    open={showDialog}
                    closeText={"No, Go Back"}
                    confirmText={"Yes, Continue"}
                />
            }
        </>
    );
};
